import { useDispatch } from "react-redux";
import styled from "styled-components";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import * as pdfjsLib from "pdfjs-dist";

import Btn from "../shared/button/Btn";
import { showModal } from "../../features/modal/modalSlice";
import questionMark from "../../images/svg/question.svg";

const Container = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.mainTextColor};

  box-sizing: border-box;

  * {
    box-sizing: border-box;
  }

  .top-container {
    width: 100%;
    min-width: 0;

    h4 {
      margin: 0;
      padding: 0 0 15px 0;
      font-size: var(--font-size-md);
      font-weight: var(--font-weight-lg);
      line-height: 1.4;
      word-break: break-word;
    }
  }

  .doc-list {
    width: 100%;
    min-width: 0;

    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 30px;

    padding: 20px 5px;

    height: 500px;
    max-height: 500px;

    overflow-y: auto;
    overflow-x: hidden;

    border-top: 1px solid
      ${({ theme }) => theme.mainTextColor};

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${({ theme }) =>
        theme.scrollThumbColor};
      border-radius: 10px;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
    }
  }

  .doc-item {
    width: 100%;
    min-width: 0;
    height: 400px;

    border: 1px solid
      ${({ theme }) => theme.mainTextColor};

    border-radius: 10px;
    overflow: hidden;

    display: flex;
    flex-direction: column;

    background-color: ${({ theme }) =>
      theme.mainBgColor || "transparent"};
  }

  .doc-item-top {
    width: 100%;
    min-height: 50px;
    height: 50px;

    flex-shrink: 0;

    border-bottom: 1px solid
      ${({ theme }) => theme.mainBorderColor};

    display: flex;
    justify-content: center;
    align-items: center;

    padding: 5px 15px;

    font-size: var(--font-size-md);
    font-weight: var(--font-weight-lg);

    text-align: center;

    p {
      margin: 0;
      padding: 0;

      width: 100%;

      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .doc-preview {
    position: relative;

    width: 100%;
    min-width: 0;

    height: calc(100% - 50px);

    flex: 1;

    overflow: hidden;

    display: flex;
    align-items: center;
    justify-content: center;
  }

  .empty-document {
    width: 100%;
    height: 100%;

    display: flex;
    justify-content: center;
    align-items: center;

    padding: 20px;

    img {
      display: block;

      width: 100%;
      height: 100%;

      max-width: 220px;
      max-height: 220px;

      object-fit: contain;

      pointer-events: none;
      user-select: none;
    }
  }

  .image-preview {
    position: relative;

    width: 100%;
    height: 100%;

    overflow: hidden;

    background-color: #111;
  }

  .background-image {
    position: absolute;

    top: 0;
    left: 0;

    width: 100%;
    height: 100%;

    object-fit: cover;

    opacity: 0.4;
    filter: brightness(40%) blur(2px);

    transform: scale(1.02);

    pointer-events: none;
    user-select: none;
  }

  .foreground-image {
    position: absolute;

    top: 0;
    left: 0;

    width: 100%;
    height: 100%;

    object-fit: contain;

    z-index: 1;

    pointer-events: none;
    user-select: none;
  }

  .pdf-preview {
    width: 100%;
    height: 100%;

    min-width: 0;

    overflow: hidden;

    .rpv-core__viewer {
      width: 100%;
      height: 100%;
    }
  }

  .btn-container {
    width: 100%;

    display: flex;
    justify-content: center;
    align-items: center;

    padding: 20px 0;

    border-top: 1px solid
      ${({ theme }) => theme.mainBorderColor};
  }

  /*
   * Büyük tablet / küçük laptop
   */
  @media (max-width: 1200px) {
    .doc-list {
      gap: 20px;
    }

    .doc-item {
      height: 360px;
    }
  }

  /*
   * Tablet
   */
  @media (max-width: 900px) {
    .doc-list {
      grid-template-columns: repeat(
        2,
        minmax(0, 1fr)
      );

      gap: 15px;

      padding: 15px 0;

      height: 480px;
      max-height: 480px;
    }

    .doc-item {
      height: 340px;
    }

    .doc-item-top {
      min-height: 48px;
      height: 48px;

      font-size: 14px;

      padding: 5px 10px;
    }

    .doc-preview {
      height: calc(100% - 48px);
    }
  }

  /*
   * Mobil
   */
  @media (max-width: 700px) {
    .top-container {
      h4 {
        font-size: 15px;
        padding-bottom: 12px;
      }
    }

    .doc-list {
      grid-template-columns: 1fr;

      gap: 15px;

      /*
       * Mobilde içerik doğal şekilde uzasın.
       * İç içe scroll problemi oluşmasın.
       */
      height: auto;
      max-height: none;

      overflow: visible;

      padding: 15px 0;
    }

    .doc-item {
      height: 420px;
    }

    .doc-item-top {
      height: auto;
      min-height: 50px;

      padding: 8px 10px;

      p {
        white-space: normal;
        overflow: visible;
        text-overflow: unset;

        line-height: 1.3;
      }
    }

    .doc-preview {
      height: auto;
      min-height: 350px;
    }

    .btn-container {
      padding: 15px 0;
    }
  }

  /*
   * Küçük mobil
   */
  @media (max-width: 480px) {
    .top-container {
      h4 {
        font-size: 14px;
      }
    }

    .doc-list {
      gap: 12px;
      padding: 12px 0;
    }

    .doc-item {
      height: 370px;
      border-radius: 8px;
    }

    .doc-item-top {
      min-height: 48px;

      font-size: 13px;

      padding: 7px 8px;
    }

    .doc-preview {
      min-height: 310px;
    }

    .empty-document {
      padding: 30px;

      img {
        max-width: 160px;
        max-height: 160px;
      }
    }

    .btn-container {
      width: 100%;

      padding: 15px 0 5px;

      > * {
        width: 100% !important;
        max-width: 100% !important;
      }
    }
  }

  /*
   * Çok küçük telefonlar
   */
  @media (max-width: 360px) {
    .doc-item {
      height: 330px;
    }

    .doc-preview {
      min-height: 270px;
    }

    .doc-item-top {
      font-size: 12px;
    }
  }
`;

const RegularDriverItemDocs = ({ uyeItemData }) => {
  const dispatch = useDispatch();
console.log(uyeItemData);

  /*
   * Bu component yalnızca görüntüleme amaçlıdır.
   * Dosya seçme, silme veya güncelleme işlemi yoktur.
   */
  const fileList = [
    {
      loadedBefore: uyeItemData?.kimlik || null,
      fileTypeName: "Kimlik",
      staticValue: "kimlik",
    },
    {
      loadedBefore: uyeItemData?.ehliyet || null,
      fileTypeName: "Ehliyet",
      staticValue: "ehliyet",
    },
    {
      loadedBefore:
        uyeItemData?.surucuBelgesi || null,
      fileTypeName: "SRC Belgesi",
      staticValue: "src",
    },
    {
      loadedBefore: uyeItemData?.fatura || null,
      fileTypeName: "Referans Faturası",
      staticValue: "fatura",
    },
    {
      loadedBefore:
        uyeItemData?.vergiLevhası || null,
      fileTypeName: "Vergi Levhası",
      staticValue: "vergiLevhası",
    },
    {
      loadedBefore: uyeItemData?.diger1 || null,
      fileTypeName: "Diğer-1",
      staticValue: "diğer-1",
    },
    {
      loadedBefore: uyeItemData?.diger2 || null,
      fileTypeName: "Diğer-2",
      staticValue: "diğer-2",
    },
  ];

  /*
   * Dosyanın PDF olup olmadığını
   * uzantısından kontrol ediyoruz.
   */
  const isPdfFile = (filePath) => {
    if (!filePath) {
      return false;
    }

    const cleanPath = filePath
      .split("?")[0]
      .split("#")[0]
      .toLowerCase();

    return cleanPath.endsWith(".pdf");
  };

  /*
   * Backend tam URL döndürüyorsa direkt kullan.
   *
   * Sadece path dönüyorsa
   * REACT_APP_API_IMG_URI ile birleştir.
   */
  const getPreviewUrl = (filePath) => {
    if (!filePath) {
      return null;
    }

    if (
      filePath.startsWith("http://") ||
      filePath.startsWith("https://") ||
      filePath.startsWith("blob:")
    ) {
      return filePath;
    }

    return `${process.env.REACT_APP_API_IMG_URI}${filePath}`;
  };

  return (
    <Container>
      <div className="top-container">
        <h4>
          Adı Soyadı:{" "}
          {uyeItemData?.adSoyadUnvan || "-"}
        </h4>

        <div className="doc-list">
          {fileList.map((fileItem) => {
            const previewSrc = getPreviewUrl(
              fileItem.loadedBefore
            );

            const isPdf = isPdfFile(
              fileItem.loadedBefore
            );

            return (
              <div
                className="doc-item"
                key={fileItem.staticValue}
              >
                <div className="doc-item-top">
                  {fileItem.loadedBefore ? (
                    <p>
                      {fileItem.fileTypeName}
                    </p>
                  ) : (
                    <p>
                      Belge Bulunamadı - (
                      {fileItem.fileTypeName})
                    </p>
                  )}
                </div>

                <div className="doc-preview">
                  {!previewSrc ? (
                    <div className="empty-document">
                      <img
                        src={questionMark}
                        alt={`${fileItem.fileTypeName} bulunamadı`}
                      />
                    </div>
                  ) : isPdf ? (
                    <div className="pdf-preview">
                      <Worker
                        workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`}
                      >
                        <Viewer
                          fileUrl={previewSrc}
                        />
                      </Worker>
                    </div>
                  ) : (
                    <div className="image-preview">
                      {/*
                       * Arka plan görseli:
                       * Boş alanların daha hoş
                       * görünmesini sağlıyor.
                       */}
                      <img
                        src={previewSrc}
                        alt=""
                        className="background-image"
                      />

                      {/*
                       * Asıl belge görüntüsü.
                       */}
                      <img
                        src={previewSrc}
                        alt={
                          fileItem.fileTypeName
                        }
                        className="foreground-image"
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="btn-container">
        <Btn
          handleClick={() =>
            dispatch(showModal(null))
          }
          width={"220px"}
          bgColor="transparent"
          borderColor="#E85446"
          color="#E85446"
          padding="10px"
        >
          Kapat
        </Btn>
      </div>
    </Container>
  );
};

export default RegularDriverItemDocs;